import { h } from 'preact';
import { CheckCircleIcon, XCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid';

export const passwordRequirements = [
    {
        label: "At least 6 characters",
        test: (pw) => pw.length >= 6
    },
    {
        label: "An upper case character",
        test: (pw) => /[A-Z]/.test(pw)
    },
    {
        label: "A numeric character",
        test: (pw) => /[0-9]/.test(pw)
    },
    {
        label: "A special character",
        tooltip: "^ $ * . [ ] { } ( ) ? \" ! @ # % & / \\ , > < ' : ; | _ ~ `",
        test: (pw) => /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`]/.test(pw)
    }
];

export function isPasswordValid(password) {
    return passwordRequirements.every((req) => req.test(password));
}

export function getPasswordStrength(password) {
    const passedCount = passwordRequirements.filter(req => req.test(password)).length;
    const total = passwordRequirements.length;
    return Math.round((passedCount / total) * 100);
}

export function validateNewPassword({ password, confirmPassword }) {
    if (!password || !confirmPassword) {
        return { ok: false, message: 'Please fill in both password fields.' };
    }
    if (password !== confirmPassword) {
        return { ok: false, message: 'Passwords do not match.' };
    }
    if (!isPasswordValid(password)) {
        return { ok: false, message: 'Password does not meet the required rules.' };
    }
    return { ok: true, message: null };
}

export function validateAccount({ email, currentPassword, password, confirmPassword, requireEmail = false, requireCurrent = false }) {
    if (requireEmail && !email) {
        return { ok: false, message: 'Please enter an email address.' };
    }
    if (requireCurrent && !currentPassword) {
        return { ok: false, message: 'Please fill in the current password field' };
    }
    return validateNewPassword({ password, confirmPassword });
}

export function PasswordRequirements({ password }) {
    if (!password) return null;

    // reuse the exported helper so logic stays in one place
    const percent = getPasswordStrength(password);
    let barColor = "bg-neutral-500";
    if (percent === 100) barColor = "bg-green-400";
    else if (percent >= 67) barColor = "bg-yellow-400";

    return (
        <div>
            <div
                role="progressbar"
                aria-label="Password strength"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                className="w-full h-1 rounded bg-gray-300 mb-2">
                <div
                    className={`h-1 rounded ${barColor}`}
                    style={{ width: `${percent}%`, transition: "width 0.3s" }}
                ></div>
            </div>
            <div className="flex flex-col space-y-2">
                {passwordRequirements.map((req, idx) => {
                    const passed = req.test(password);
                    return (
                        <div key={idx}>
                            <div className="flex items-center space-x-1">
                                {passed ? (
                                    <CheckCircleIcon className="w-4 h-4 text-green-400" />
                                ) : (
                                    <XCircleIcon className="w-4 h-4 text-neutral-500" />
                                )}
                                <span className={`text-xs ${passed ? "text-green-400" : "text-neutral-500"}`}>{req.label}</span>
                                {req.tooltip && (
                                    <button
                                        type="button"
                                        aria-label="Show allowed special characters"
                                        className="inline-flex"
                                        onClick={(e) => {
                                            e.currentTarget.parentElement.nextElementSibling._toggle();
                                        }}
                                    >
                                        <QuestionMarkCircleIcon
                                            className="w-4 h-4 text-neutral-400"
                                            aria-hidden="true"
                                        />
                                    </button>
                                )}
                            </div>
                            {req.tooltip && (
                                <div className="grid transition-all duration-300 ml-5 mt-1"
                                     style={{gridTemplateRows: '0fr'}}
                                     ref={(el) => { if (el) el._toggle = () => el.style.gridTemplateRows = el.style.gridTemplateRows === '0fr' ? '1fr' : '0fr'; }}>
                                    <div className="overflow-hidden">
                                        <div className="text-xs text-neutral-300 bg-neutral-800 rounded px-3 py-2 mb-1">
                                            A special character is one of:<br/>
                                            <span className="text-neutral-400">{req.tooltip}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default PasswordRequirements;
